/**
 * Вывод реферального заработка: подписи способов и проверка реквизитов.
 *
 * Проверки повторяют серверные (`application/referrals/withdrawals.validate_destination`)
 * и нужны только для подсказки под полем до отправки. Решает всё равно сервер: если
 * правила разъедутся, человек увидит его отказ у того же поля, а не потеряет заявку.
 */
import i18n from '@/i18n';
import { ApiError, type WithdrawMethod, type WithdrawStatus } from '@/lib/api';

/** Один ключ на главную, профиль и экран вывода: ответ мутаций кладётся прямо в него. */
export const WITHDRAW_QUERY_KEY = ['referral-withdraw'] as const;

export function withdrawMethodLabel(method: WithdrawMethod | string): string {
  if (method === 'card') return i18n.t('Withdraw method card');
  if (method === 'sbp') return i18n.t('Withdraw method sbp');
  if (method === 'usdt_trc20') return i18n.t('Withdraw method usdt_trc20');
  if (method === 'ton') return i18n.t('Withdraw method ton');
  if (method === 'cryptobot') return i18n.t('Withdraw method cryptobot');
  return method;
}

export function withdrawStatusLabel(status: WithdrawStatus | string): string {
  if (status === 'pending') return i18n.t('Withdraw status pending');
  if (status === 'processing') return i18n.t('Withdraw status processing');
  if (status === 'paid') return i18n.t('Withdraw status paid');
  if (status === 'rejected') return i18n.t('Withdraw status rejected');
  if (status === 'cancelled') return i18n.t('Withdraw status cancelled');
  return status;
}

/**
 * Отменить можно только заявку, которую ещё не начали переводить. На `processing`
 * владелец сервиса уже отправляет деньги: отмена вернула бы сумму на баланс поверх
 * перевода, и бэк её отбивает (`app.referral_withdraw.processing`).
 */
export function canCancelWithdrawal(status: WithdrawStatus | string): boolean {
  return status === 'pending';
}

// ─── Карта ──────────────────────────────────────────────────────────────────
export const CARD_MAX_DIGITS = 19;

/** Только цифры, не длиннее самого длинного номера карты. */
export function cardDigits(raw: string): string {
  return raw.replace(/\D/g, '').slice(0, CARD_MAX_DIGITS);
}

/** «2200123412341234» → «2200 1234 1234 1234», как на самой карте. */
export function groupCard(digits: string): string {
  return digits.replace(/(\d{4})(?=\d)/g, '$1 ');
}

export function luhnOk(digits: string): boolean {
  let total = 0;
  for (let i = 0; i < digits.length; i++) {
    let value = Number(digits[digits.length - 1 - i]);
    if (i % 2 === 1) {
      value *= 2;
      if (value > 9) value -= 9;
    }
    total += value;
  }
  return total % 10 === 0;
}

export function isValidCard(digits: string): boolean {
  return /^\d{16,19}$/.test(digits) && luhnOk(digits);
}

// ─── СБП ────────────────────────────────────────────────────────────────────
/** Российский номер в виде `+7XXXXXXXXXX` или null. Принимает 8…, 9… и +7…. */
export function normalizePhone(raw: string): string | null {
  let digits = raw.replace(/\D/g, '');
  if (digits.length === 10 && digits.startsWith('9')) digits = `7${digits}`;
  else if (digits.length === 11 && digits.startsWith('8')) digits = `7${digits.slice(1)}`;
  if (digits.length !== 11 || !digits.startsWith('7')) return null;
  return `+${digits}`;
}

export const BANK_MIN = 2;
export const BANK_MAX = 40;

/** Название банка без лишних пробелов, как его сохранит сервер. */
export function normalizeBank(raw: string): string {
  return raw.trim().split(/\s+/).filter(Boolean).join(' ');
}

export function isValidBank(raw: string): boolean {
  const length = [...normalizeBank(raw)].length;
  return length >= BANK_MIN && length <= BANK_MAX;
}

// ─── USDT TRC20 ─────────────────────────────────────────────────────────────
const TRC20_RE = /^T[1-9A-HJ-NP-Za-km-z]{33}$/;

export function isValidTrc20(raw: string): boolean {
  return TRC20_RE.test(raw.trim());
}

// ─── GRAM (TON) ─────────────────────────────────────────────────────────────
// Адрес кошелька: user-friendly форма (48 символов base64url, начинается с EQ/UQ/kQ/0Q)
// или raw (`0:` и 64 hex). Сервер всё равно разбирает адрес сам, это подсказка у поля.
const TON_FRIENDLY_RE = /^[EUk0]Q[A-Za-z0-9_-]{46}$/;
const TON_RAW_RE = /^-?\d+:[0-9a-fA-F]{64}$/;
export const MEMO_MAX = 120;

export function isValidTonAddress(raw: string): boolean {
  const value = raw.trim();
  return TON_FRIENDLY_RE.test(value) || TON_RAW_RE.test(value);
}

/** Memo как его сохранит сервер: без лишних пробелов. */
export function normalizeMemo(raw: string): string {
  return raw.trim().split(/\s+/).filter(Boolean).join(' ');
}

/** Способы, которые сервис может заплатить сам: комиссия сети из суммы. */
export function isPlatformMethod(method: WithdrawMethod | string | null): boolean {
  return method === 'ton' || method === 'cryptobot';
}

// ─── Ошибки сервера ─────────────────────────────────────────────────────────
/** Код и контекст отказа (`{error: {code, message, context}}`). */
export function withdrawErrorOf(e: unknown): { code: string | null; context: Record<string, unknown> } {
  if (!(e instanceof ApiError)) return { code: null, context: {} };
  const context = (e.body as { error?: { context?: Record<string, unknown> } } | null)?.error?.context;
  return { code: e.code, context: context && typeof context === 'object' ? context : {} };
}
